import { useState, useEffect } from 'react';
import axios from 'axios';

// Very conservative fallback when no model configuration is available
const CONSERVATIVE_FALLBACK = { 
  contextWindow: 8000, 
  softLimit: 6000, 
  hardLimit: 7500 
};

// Default context windows for core engine models (fallback when API fails)
const DEFAULT_CORE_ENGINE_MODELS = {
  'auto': {
    contextWindow: 128000,
    softLimit: Math.floor(128000 * 0.8),
    hardLimit: Math.floor(128000 * 0.95)
  },
  'gpt-4o': {
    contextWindow: 128000,
    softLimit: Math.floor(128000 * 0.8),
    hardLimit: Math.floor(128000 * 0.95)
  },
  'claude-3-5-sonnet-20241022': {
    contextWindow: 200000,
    softLimit: Math.floor(200000 * 0.8),
    hardLimit: Math.floor(200000 * 0.95)
  },
  'gemini-1.5-pro': {
    contextWindow: 2000000,
    softLimit: Math.floor(2000000 * 0.8),
    hardLimit: Math.floor(2000000 * 0.95)
  }
};

export const useModelContextWindows = () => {
  const [modelContextWindows, setModelContextWindows] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch model configurations from the backend
  const fetchModelContextWindows = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.log('No auth token, using default context windows');
        setIsLoading(false);
        return;
      }

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/context-windows`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data && typeof response.data === 'object') {
        // Merge backend data with defaults
        const backendContextWindows = {};
        
        Object.entries(response.data).forEach(([modelKey, config]) => {
          if (config.contextWindow) {
            backendContextWindows[modelKey] = {
              contextWindow: config.contextWindow,
              softLimit: config.softLimit || Math.floor(config.contextWindow * 0.8),
              hardLimit: config.hardLimit || Math.floor(config.contextWindow * 0.95)
            };
          }
        });

        setModelContextWindows(backendContextWindows);

        console.log('Loaded model context windows from backend:', backendContextWindows);
      }
    } catch (err) {
      console.warn('Failed to fetch model context windows from backend, using defaults:', err.message);
      setError(err.message);
      // Continue with defaults
    } finally {
      setIsLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    fetchModelContextWindows();
  }, []);

  // Get context window config for a specific model
  const getModelConfig = (modelName) => {
    if (!modelName) return CONSERVATIVE_FALLBACK;
    
    // Check default core engine models first (includes 'auto')
    if (DEFAULT_CORE_ENGINE_MODELS[modelName]) {
      return DEFAULT_CORE_ENGINE_MODELS[modelName];
    }
    
    // Normalize model name for lookup
    const normalizedModel = modelName.toLowerCase().replace(/[^a-z0-9-]/g, '-');
    
    // Try exact match in loaded models first
    if (modelContextWindows[normalizedModel]) {
      return modelContextWindows[normalizedModel];
    }
    
    // Try partial matches for model families (e.g., gpt-4 matches gpt-4o)
    const modelKey = Object.keys(modelContextWindows).find(key => 
      normalizedModel.includes(key.split('-')[0]) || key.includes(normalizedModel.split('-')[0])
    );
    
    if (modelKey && modelContextWindows[modelKey]) {
      return modelContextWindows[modelKey];
    }
    
    // Check if it's a core engine model by name pattern
    const coreEngineModelKey = Object.keys(DEFAULT_CORE_ENGINE_MODELS).find(key =>
      modelName.includes(key) || key.includes(modelName.split('-')[0])
    );
    
    if (coreEngineModelKey) {
      return DEFAULT_CORE_ENGINE_MODELS[coreEngineModelKey];
    }
    
    // If no configuration found, return conservative fallback
    console.warn(`No context window configuration found for model: ${modelName}, using conservative fallback`);
    return CONSERVATIVE_FALLBACK;
  };

  // Update a specific model's context window (for admin users)
  const updateModelConfig = async (modelName, config) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token');

      await axios.put(
        `${process.env.REACT_APP_API_URL}/context-windows/${modelName}`,
        config,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      // Update local state
      const normalizedModel = modelName.toLowerCase().replace(/[^a-z0-9-]/g, '-');
      setModelContextWindows(prev => ({
        ...prev,
        [normalizedModel]: {
          contextWindow: config.contextWindow,
          maxOutputTokens: config.maxOutputTokens,
          softLimit: config.softLimit || Math.floor(config.contextWindow * 0.8),
          hardLimit: config.hardLimit || Math.floor(config.contextWindow * 0.95)
        }
      }));

      return true;
    } catch (err) {
      console.error('Failed to update model config:', err);
      setError(err.message);
      return false;
    }
  };

  return {
    modelContextWindows,
    getModelConfig,
    updateModelConfig,
    refetch: fetchModelContextWindows,
    isLoading,
    error
  };
};